import { parseActiveRopaReviewMetricsForFE, parseAssetRopaReviewForFE } from 'config/api/ropaReviews/ropaReviews.parsers';
import type {
  ActiveRopaReviewMetrics,
  ActiveRopaReviewMetricsDTO,
  AdminAssetRopaReviewListDTO,
  RopaReviewRoundStatus,
} from 'config/api/ropaReviews/ropaReviews.types';

import type { OwnerAssetRopaListResponse, OwnerAssetRopaMetrics } from './assetRopa.types';
import type { OwnerWorkspaceAsset, OwnerWorkspaceListResponse, OwnerWorkspaceMetrics } from './assetRopaWorkspace.types';

export const EMPTY_OWNER_METRICS: OwnerAssetRopaMetrics = {
  includedAssetsCount: 0,
  awaitingResponseCount: 0,
  submittedCount: 0,
  acceptedCount: 0,
  clarificationRequestedCount: 0,
  assetsWithWarningsCount: 0,
};

export const EMPTY_WORKSPACE_METRICS: OwnerWorkspaceMetrics = {
  gdprRelevantAssetsCount: 0,
  assetsWithoutProcessingActivitiesCount: 0,
  incompleteProcessingsCount: 0,
  dpiaIndicationsCount: 0,
  thirdCountryProcessingCount: 0,
  sensitivePersonalDataCount: 0,
};

const asRecord = (value: unknown): Record<string, unknown> | null => {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    return null;
  }

  return value as Record<string, unknown>;
};

const readNumber = (value: unknown, fallback = 0): number => {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value;
  }

  if (typeof value === 'string' && value.trim() !== '' && Number.isFinite(Number(value))) {
    return Number(value);
  }

  return fallback;
};

const isSnakeCaseMetrics = (metrics: Record<string, unknown>): metrics is ActiveRopaReviewMetricsDTO =>
  'included_assets_count' in metrics ||
  'awaiting_response_count' in metrics ||
  'submitted_count' in metrics ||
  'accepted_count' in metrics ||
  'clarification_requested_count' in metrics ||
  'assets_with_warnings_count' in metrics;

/** Accepts BE snake_case DTO or already-camelCase FE metrics. */
export const parseOwnerAssetRopaMetricsForFE = (raw: unknown): OwnerAssetRopaMetrics => {
  const metrics = asRecord(raw);
  if (!metrics) {
    return EMPTY_OWNER_METRICS;
  }

  if (isSnakeCaseMetrics(metrics)) {
    return parseActiveRopaReviewMetricsForFE(metrics);
  }

  return {
    includedAssetsCount: readNumber(metrics.includedAssetsCount),
    awaitingResponseCount: readNumber(metrics.awaitingResponseCount),
    submittedCount: readNumber(metrics.submittedCount),
    acceptedCount: readNumber(metrics.acceptedCount),
    clarificationRequestedCount: readNumber(metrics.clarificationRequestedCount),
    assetsWithWarningsCount: readNumber(metrics.assetsWithWarningsCount),
  };
};

export const parseOwnerWorkspaceMetricsForFE = (raw: unknown): OwnerWorkspaceMetrics => {
  const metrics = asRecord(raw);
  if (!metrics) {
    return EMPTY_WORKSPACE_METRICS;
  }

  return {
    gdprRelevantAssetsCount: readNumber(metrics.gdpr_relevant_assets_count ?? metrics.gdprRelevantAssetsCount),
    assetsWithoutProcessingActivitiesCount: readNumber(
      metrics.assets_without_processing_activities_count ?? metrics.assetsWithoutProcessingActivitiesCount,
    ),
    incompleteProcessingsCount: readNumber(metrics.incomplete_processings_count ?? metrics.incompleteProcessingsCount),
    dpiaIndicationsCount: readNumber(metrics.dpia_indications_count ?? metrics.dpiaIndicationsCount),
    thirdCountryProcessingCount: readNumber(metrics.third_country_processing_count ?? metrics.thirdCountryProcessingCount),
    sensitivePersonalDataCount: readNumber(metrics.sensitive_personal_data_count ?? metrics.sensitivePersonalDataCount),
  };
};

type ActiveReviewPayload = {
  id: number;
  status: RopaReviewRoundStatus;
  deadline: string;
  review_instructions?: string;
  reviewInstructions?: string;
  initiated_at?: string;
  initiatedAt?: string;
};

const parseActiveReview = (raw: unknown): OwnerAssetRopaListResponse['activeReview'] => {
  const review = asRecord(raw) as ActiveReviewPayload | null;
  if (!review || typeof review.id !== 'number') {
    return null;
  }

  return {
    id: review.id,
    status: review.status,
    deadline: review.deadline,
    reviewInstructions: review.review_instructions ?? review.reviewInstructions ?? '',
    initiatedAt: review.initiated_at ?? review.initiatedAt ?? '',
  };
};

const parseAssetsSource = (payload: Record<string, unknown>, data: unknown): AdminAssetRopaReviewListDTO[] => {
  if (Array.isArray(payload.assets)) {
    return payload.assets as AdminAssetRopaReviewListDTO[];
  }

  if (Array.isArray(payload.results)) {
    return payload.results as AdminAssetRopaReviewListDTO[];
  }

  if (Array.isArray(data)) {
    return data as AdminAssetRopaReviewListDTO[];
  }

  return [];
};

const parseOwnerWorkspaceAssetForFE = (dto: AdminAssetRopaReviewListDTO & Record<string, unknown>): OwnerWorkspaceAsset => {
  const base = parseAssetRopaReviewForFE(dto);
  const gdprRelevanceStatus =
    (dto.gdpr_relevance_status as OwnerWorkspaceAsset['gdprRelevanceStatus'] | undefined) ??
    (dto.gdprRelevanceStatus as OwnerWorkspaceAsset['gdprRelevanceStatus'] | undefined) ??
    'currently_indicated';
  const informationalCompleteness =
    (dto.informational_completeness as OwnerWorkspaceAsset['informationalCompleteness'] | undefined) ??
    (dto.informationalCompleteness as OwnerWorkspaceAsset['informationalCompleteness'] | undefined) ??
    (base.processingActivitiesCount > 0 ? 'incomplete' : 'no_processing_activities');

  return {
    ...base,
    gdprRelevanceStatus,
    informationalCompleteness,
    lastUpdatedAt: String(dto.last_updated_at ?? dto.lastUpdatedAt ?? dto.updated_at ?? ''),
    lastUpdatedByName:
      (dto.last_updated_by_name as string | null | undefined) ?? (dto.lastUpdatedByName as string | null | undefined) ?? null,
  };
};

export const parseOwnerListResponse = (data: unknown): OwnerAssetRopaListResponse => {
  if (!data || typeof data !== 'object') {
    throw new Error('Invalid asset-ropa/mine response payload.');
  }

  const payload = data as Record<string, unknown>;
  const assetsSource = parseAssetsSource(payload, data);

  return {
    activeReview: parseActiveReview(payload.active_review ?? payload.activeReview),
    metrics: parseOwnerAssetRopaMetricsForFE(payload.metrics),
    assets: assetsSource.map(item => parseAssetRopaReviewForFE(item)),
  };
};

export const parseOwnerWorkspaceResponse = (data: unknown): OwnerWorkspaceListResponse => {
  if (!data || typeof data !== 'object') {
    throw new Error('Invalid asset-ropa/mine workspace response payload.');
  }

  const payload = data as Record<string, unknown>;
  const base = parseOwnerListResponse(data);
  const assetsSource = parseAssetsSource(payload, data);

  return {
    ...base,
    workspaceMetrics: parseOwnerWorkspaceMetricsForFE(payload.workspace_metrics ?? payload.workspaceMetrics),
    assets: assetsSource.map(item => parseOwnerWorkspaceAssetForFE(item as AdminAssetRopaReviewListDTO & Record<string, unknown>)),
  };
};

export const parseOwnerMineDetailForFE = (data: unknown): OwnerWorkspaceAsset => {
  if (!data || typeof data !== 'object') {
    throw new Error('Invalid asset-ropa/mine detail response payload.');
  }

  const payload = asRecord(data) ?? {};
  const nestedAsset = asRecord(payload.asset) ?? asRecord(payload.result) ?? payload;

  return parseOwnerWorkspaceAssetForFE(nestedAsset as AdminAssetRopaReviewListDTO & Record<string, unknown>);
};

export const parseOwnerMineMetricsResponseForFE = (
  data: unknown,
): {
  metrics: ActiveRopaReviewMetrics;
  workspaceMetrics?: OwnerWorkspaceMetrics;
} => {
  if (!data || typeof data !== 'object') {
    throw new Error('Invalid asset-ropa/mine/metrics response payload.');
  }

  const payload = data as Record<string, unknown>;
  const metricsRaw = payload.metrics ?? payload;
  const hasWorkspaceMetrics = 'workspace_metrics' in payload || 'workspaceMetrics' in payload;

  return {
    metrics: parseOwnerAssetRopaMetricsForFE(metricsRaw),
    ...(hasWorkspaceMetrics
      ? { workspaceMetrics: parseOwnerWorkspaceMetricsForFE(payload.workspace_metrics ?? payload.workspaceMetrics) }
      : {}),
  };
};
